import * as _ from 'underscore';
import { Order } from '../../ts/helpers/Order';
import { ServerFilterClauses } from '../../ts/helpers/ServerFilterClauses';
import { ContentFilterKeyName, IContentFilters } from '../../ts/models/ContentFilters';
import { IContentType } from '../../ts/models/ContentTypes';
import { ISubplace } from '../../ts/models/IPlace';
import { Module } from '../../ts/Module';
import { IListAndFieldValuesResource } from '../../ts/resources/Resource';
import { IResourceQuery } from '../../ts/resources/ResourceQuery';
import { IFilterClause, ILeafFilterClause } from '../../ts/ServerApi/types/IFilterClause';
import { IOrderClause } from '../../ts/ServerApi/types/IOrderClause';

export interface ISortableParams {
  order?: string;
}

export class QueryService {

  public static $inject = ['$location'];

  constructor(private $location: ng.ILocationService) {
  }

  public createQueryUpdaterFromSubplace<T>(
    $scope: ng.IScope,
    subplace: ISubplace<T>,
    refreshFn: (resourceQuery: IResourceQuery<T>) => any
    ): IQueryUpdater {

    return new QueryUpdater<T>(
      this.$location,
      $scope,
      subplace.type,
      subplace.resource,
      refreshFn,
      subplace.defaultFilters,
      subplace.filterKeys()
    );
  }

  public createQueryUpdater<T>(
      $scope: ng.IScope,
      type: IContentType,
      resource: IListAndFieldValuesResource<T>,
      refreshFn: (resourceQuery: IResourceQuery<T>) => any,
      defaultFilters: Partial<IContentFilters<string>>
  ): IQueryUpdater {

    return new QueryUpdater<T>(
      this.$location,
      $scope,
      type,
      resource,
      refreshFn,
      defaultFilters);
  }
}

export interface IQueryUpdater {
  refresh(): void;
  anyNonDefaultFiltersActive(): boolean;
}

class QueryUpdater<T> implements IQueryUpdater {

  // store last order and filters so we know if we will need to update the resource query
  private order: IOrderClause[] = null;
  private filters: ILeafFilterClause[] = null;

  constructor(
    private $location: ng.ILocationService,
    $scope: ng.IScope,
    private type: IContentType,
    private resource: IListAndFieldValuesResource<T>,
    private refreshFn: (resourceQuery: IResourceQuery<T>) => any,
    private defaultFilters: Partial<IContentFilters<string>>,
    private filterKeys: ContentFilterKeyName[] = type.filterKeys
  ) {

    $scope.$on('$locationChangeSuccess', (event: ng.IAngularEvent, newLoc: string, oldLoc: string) => {
      // Only trigger a refresh if we're at the same location
      // i.e. only the query string has changed
      newLoc = newLoc.split('?')[0];
      oldLoc = oldLoc.split('?')[0];
      if (newLoc === oldLoc) {
        this.refreshIfOrderOrFilterChanged();
      }
    });
  }

  // public methods

  public refresh() {
    const orderAndFilters = this.getCurrentOrderAndFilters();
    const order = orderAndFilters.order;
    const filters = orderAndFilters.filters;
    const extraQueryParams = this.type.extraQueryParams;

    this.rememberOrderAndFilter(order, filters);
    const resourceQuery = this.createResourceQuery(order, filters, extraQueryParams);
    this.refreshFn(resourceQuery);
  }

  /**
   * Returns true if any filters have been set through URL parameters.
   */
  public anyNonDefaultFiltersActive(): boolean {
    const nonDefaultFilters = this.getFiltersFromUrlParams(this.$location.search(), this.filterKeys);
    return Object.keys(nonDefaultFilters).length > 0;
  }

  // private methods

  private refreshIfOrderOrFilterChanged() {
    const orderAndFilters = this.getCurrentOrderAndFilters();
    const order = orderAndFilters.order;
    const filters = orderAndFilters.filters;
    const extraQueryParams = this.type.extraQueryParams;

    if (this.didOrderOrFilterChanged(order, filters)) {
      this.rememberOrderAndFilter(order, filters);
      const resourceQuery = this.createResourceQuery(order, filters, extraQueryParams);
      this.refreshFn(resourceQuery);
    }
  }

  private didOrderOrFilterChanged(
    newOrder: IOrderClause[],
    newFilters: IFilterClause[]): boolean {

    return (!_.isEqual(newOrder, this.order) || !_.isEqual(newFilters, this.filters));
  }

  private rememberOrderAndFilter(
    order: IOrderClause[],
    filters: IFilterClause[]) {

    this.order = order;
    this.filters = filters;
  }

  private createResourceQuery(order: IOrderClause[], filters: IFilterClause[],
    extraParams?: any) {
    return this.resource.all().withFilters(filters).withOrder(order).withExtraParams(extraParams);
  }

  private getCurrentOrderAndFilters(): any {
    const urlParams: ISortableParams = this.$location.search();
    return {
      order: Order.fromInput(urlParams.order, this.type.orderKeys) || this.type.defaultOrder,
      filters: this.getFilterClauses(urlParams, this.filterKeys, this.defaultFilters)
    };
  }

  private getFilterClauses(
    urlParams: ISortableParams,
    filterKeys: ContentFilterKeyName[],
    defaultFilters: Partial<IContentFilters<string>> = {}
  ): ILeafFilterClause[] {
    const urlParamsThatAreFilters = this.getFiltersFromUrlParams(urlParams, filterKeys);

    const filtersWithDefaultsAdded = _.defaults(urlParamsThatAreFilters, defaultFilters);
    const filterClauses = ServerFilterClauses.create(filtersWithDefaultsAdded);
    return filterClauses;
  }

  private getFiltersFromUrlParams(urlParams: ISortableParams, filterKeys: ContentFilterKeyName[]) {
    return _(urlParams).pick(filterKeys);
  }
}

Module.instance().service('QueryService', QueryService);
